import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { db } from '../firebaseConfig';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { ReactComponent as ArrowIcon } from '../assets/icons/ArrowIcon.svg';
import LoadingSpinner from '../assets/LoadingSpinner';
import { leerCache, guardarEnCache, obtenerConCache, CLAVE_VINOS, CLAVE_DETALLE } from '../services/cacheDatos';
import {
    collection,
    query,
    where,
    getDocs,
    doc,
    getDoc
} from 'firebase/firestore';
import { useDetalleHistorial } from '../hooks/useDetalleHistorial';
import { formatCepas, precargarImagenes, filtrarVinosDemo, obtenerMarcaYVariedad, CantidadStepper } from './Cupo';

// Shop es la tienda general: accesible para cualquier usuario logueado
// (Registrado, Espera o Activo), a diferencia de /cupo-asignado (Cupo asignado)
// que exige estadoLista='Activo'. No aplica límite de cupo (cupos_vinos
// solo lo tienen los miembros de la lista) — la compra queda limitada
// únicamente por stock, salvo que el usuario sí tenga cupo asignado.
// 'Crema de Estrellas' queda afuera: es exclusivo de Cupo asignado.
const TIPOS_TINTO = ['Red', 'Red Blend'];
const TIPOS_BLANCO = ['White', 'White Blend'];

const getCupoForVino = (vinoId, cuposVinosUsuario) => {
    if (!cuposVinosUsuario || cuposVinosUsuario.length === 0) {
        return null;
    }
    const cupoEncontrado = cuposVinosUsuario.find(cupo => cupo.id === vinoId);
    return cupoEncontrado ? cupoEncontrado.cupo : null;
};

// DetalleVino vive FUERA de Shop a proposito: si se redefiniera adentro
// (como estaba antes), React ve una funcion de componente distinta en cada
// render del padre y desmonta/remonta todo el detalle -- eso es lo que hacia
// que la pantalla saltara al top al tocar "Agregar" (que dispara un re-render
// via el contexto del carrito). Como componente de modulo, la referencia es
// estable entre renders y no hay remount.
const DetalleVino = ({ vino, onVolver, cantidades, onCantidadChange, onAddToCart, usaNavbarPublica }) => {
    const [datosOrigen, setDatosOrigen] = useState(null);
    const [cargandoDetalle, setCargandoDetalle] = useState(true);
    useEffect(() => {
        // El origen/trazabilidad de un vino no cambia nunca (cosecha,
        // embotellado, graduacion), asi que una vez traido queda guardado para
        // toda la visita: entrar y salir del detalle ya no re-consulta las
        // cuatro colecciones anidadas.
        const claveDetalle = `${CLAVE_DETALLE}${vino?.id}`;
        const cacheado = vino?.id ? leerCache(claveDetalle, Infinity) : null;
        if (cacheado) {
            setDatosOrigen(cacheado.valor);
            setCargandoDetalle(false);
            return;
        }
        const aplicar = (datos) => {
            if (vino?.id) guardarEnCache(claveDetalle, datos);
            setDatosOrigen(datos);
        };
        const fetchDetallesAnidados = async () => {
            if (!vino || !vino.trazabilidad_id) {
                // Sin cadena de trazabilidad (vinos demo, o cualquier vino
                // cargado sin ese vínculo): si el propio doc trae estos datos
                // sueltos, los usamos igual en vez de dejar todo en blanco.
                if (vino && (vino.fecha_cosecha || vino.fecha_embotellado || vino.graduacion_alcoholica)) {
                    aplicar({
                        fecha_cosecha: vino.fecha_cosecha || null,
                        fecha_embotellado: vino.fecha_embotellado || null,
                        graduacion_alcoholica: vino.graduacion_alcoholica ?? null,
                        recipiente_utilizado: vino.recipiente_utilizado || null,
                    });
                }
                setCargandoDetalle(false);
                return;
            }
            try {
                let graduacionAlcoholica = null;
                let recipienteUtilizado = null;
                let fechaEmbotellado = null;
                let datosViticultura = null;
                const trazDocRef = doc(db, 'trazabilidad', vino.trazabilidad_id);
                const trazDocSnap = await getDoc(trazDocRef);
                if (!trazDocSnap.exists()) {
                    setCargandoDetalle(false);
                    return;
                }
                const trazData = trazDocSnap.data();
                const partidasUsadas = trazData.partidas_usadas;
                const embotelladoId = trazData.embotellado_id;
                if (embotelladoId) {
                    const embotelladoDocRef = doc(db, 'embotellado', embotelladoId);
                    const embotelladoDocSnap = await getDoc(embotelladoDocRef);
                    if (embotelladoDocSnap.exists()) {
                        fechaEmbotellado = embotelladoDocSnap.data().estiba_inicio;
                    }
                }
                const partidaPrincipal = partidasUsadas.reduce((max, current) => {
                    return (current.cantidad_hl > max.cantidad_hl) ? current : max;
                }, { cantidad_hl: -1 });
                if (!partidaPrincipal || !partidaPrincipal.ref) {
                    setCargandoDetalle(false);
                    return;
                }
                const vinifDocRef = doc(db, 'vinificacion', partidaPrincipal.ref);
                const vinifDocSnap = await getDoc(vinifDocRef);
                if (vinifDocSnap.exists()) {
                    const vinifData = vinifDocSnap.data();
                    const loteOrigenRef = vinifData.lote_origen_ref;
                    graduacionAlcoholica = vinifData.guarda?.graduacion_alcoholica ?? null;
                    recipienteUtilizado = vinifData.guarda?.recipiente_utilizado ?? null;
                    if (loteOrigenRef) {
                        const vitDocRef = doc(db, 'viticultura', loteOrigenRef);
                        const vitDocSnap = await getDoc(vitDocRef);
                        if (vitDocSnap.exists()) {
                            datosViticultura = vitDocSnap.data();
                        }
                    }
                }
                // Vino comprado: no hay vinificación/viticultura detrás, así que
                // vinifDocSnap nunca existe y graduacionAlcoholica/datosViticultura
                // quedan null. Para ese caso se usan los campos que se cargaron a
                // mano al crear el vino (ver DocDetail.js, sólo existen para vino
                // comprado). Si el vino es propio, la cadena de arriba siempre
                // los completa y este fallback nunca entra en juego.
                aplicar({
                    ...datosViticultura,
                    graduacion_alcoholica: graduacionAlcoholica ?? vino.graduacion_alcoholica ?? null,
                    recipiente_utilizado: recipienteUtilizado,
                    fecha_embotellado: fechaEmbotellado,
                    fecha_cosecha: datosViticultura?.fecha_cosecha || vino.fecha_cosecha || null,
                });
                setCargandoDetalle(false);
            } catch (error) {
                console.error("Error en la carga anidada:", error);
                setCargandoDetalle(false);
            }
        };
        fetchDetallesAnidados();
    }, [vino]);
    const handleGoBack = () => {
        onVolver();
    };
    if (cargandoDetalle) return <LoadingSpinner fullScreen />;
    const clasificacionFinal = vino.tipo_vino === 'Crema de Estrellas' ? vino.clasificacion_azucar : vino.clasificacion;
    const cepasFormateadas = formatCepas(vino.cepas);
    const contenidoCepasCompleto = vino.tipo_vino === 'Crema de Estrellas'
        ? 'Crema de Estrellas'
        : (cepasFormateadas && cepasFormateadas.length > 0 ? cepasFormateadas.join(' - ') : null);
    // Si el resto del nombre ("Malbec" en "Red Social Malbec") ya ES la
    // cepa, no la repetimos en su propia línea debajo.
    const { marca: marcaVino, resto: restoVino } = obtenerMarcaYVariedad(vino);
    const contenidoCepas = (contenidoCepasCompleto && contenidoCepasCompleto.toLowerCase() === restoVino.toLowerCase())
        ? null
        : contenidoCepasCompleto;
    const formatStringDate = (dateString) => {
        if (!dateString) return '';
        try {
            const dateObj = new Date(dateString + 'T00:00:00');
            return dateObj.toLocaleDateString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch (e) {
            return dateString;
        }
    }
    const fechaCosechaFormateada = formatStringDate(datosOrigen?.fecha_cosecha);
    const fechaEmbotelladoFormateada = formatStringDate(datosOrigen?.fecha_embotellado);
    const graduacion = datosOrigen?.graduacion_alcoholica;
    // Sin ceros de relleno: 11.5 se muestra "11.5%", no "11.50%" (toFixed(2)
    // siempre agrega 2 decimales aunque el valor cargado tenga menos).
    const graduacionFormateada = (typeof graduacion === 'number' && graduacion > 0)
        ? `${parseFloat(graduacion.toFixed(2))}% V/V`
        : '';
    return (
        <div className="detalle-card shop-detalle-card">
            <div
                className={`navegacion-flecha cupo${usaNavbarPublica ? ' cupo-registrado' : ''}`}
                onClick={handleGoBack}
            >
                <div>
                    <ArrowIcon className='arrow-icon shop-arrow-icon' />
                </div>
                <span className="navegacion-texto">Volver al shop</span>
            </div>
            <div className="detalle-imagen">
                {vino.etiqueta_imagen_url ? (
                    <img
                        src={vino.etiqueta_imagen_url}
                        alt={`Etiqueta de ${vino.vino_nombre}`}
                        className="detalle-wine-image"
                    />
                ) : (
                    <div className="image-placeholder">
                        <p className="data-text">Imagen no disponible</p>
                    </div>
                )}
            </div>
            <div className="detalle-datos">
                <h1 className="wine-name shop-detalle-nombre">
                    <span className="wine-name-marca">{marcaVino}</span>
                    {restoVino && (
                        <span className="wine-name-resto">{restoVino}</span>
                    )}
                </h1>
                {contenidoCepas && <p className="detalle-data-text shop-detalle-info">{contenidoCepas}</p>}
                <p className="detalle-data-text shop-detalle-info">{vino.anada}</p>
                {clasificacionFinal === 'Gran Reserva' && <p className="detalle-data-text shop-detalle-info--espaciada">{clasificacionFinal}</p>}
                <div style={{ marginTop: '20px' }}>
                    {/* Vino comprado sin fecha de cosecha cargada: no se muestra
                        la línea en vez de dejarla en blanco (a diferencia del
                        alcohol, que es obligatorio y siempre debería tener valor). */}
                    {fechaCosechaFormateada && (
                        <p className="detalle-data-text shop-detalle-meta">
                            Fecha de Cosecha: {fechaCosechaFormateada}
                        </p>
                    )}
                    <p className="detalle-data-text shop-detalle-meta">
                        Fecha de Embotellado: {fechaEmbotelladoFormateada}
                    </p>
                </div>
                {graduacionFormateada && (
                    <p className="detalle-data-text shop-detalle-meta">
                        Alcohol: {graduacionFormateada}
                    </p>
                )}
                <div className="titulo-principal shop-titulo-detalle">
                    <hr className="linea-horizontal" />
                    <h1>NOTAS DE SU CREADOR</h1>
                    <hr className="linea-horizontal" />
                </div>
                <div className="detalle-description-text">
                    <ReactMarkdown>{vino.descripcion}</ReactMarkdown>
                </div>
                <div className="detalle-compra">
                    <p className="detalle-precio">
                        {vino.precioUnidad ?
                            `AR$ ${new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(vino.precioUnidad)}`
                            : ''}
                    </p>
                    {(() => {
                        const cupoDetalle = vino.cupoAsignado;
                        const maximoCompraDetalle = cupoDetalle !== null && cupoDetalle !== undefined
                            ? Math.min(vino.cantidadUnidades, cupoDetalle)
                            : vino.cantidadUnidades;
                        return maximoCompraDetalle > 0 ? (
                            <div className="detalle-cantidad-fila">
                                <CantidadStepper
                                    value={cantidades[vino.id] || 1}
                                    min={1}
                                    max={maximoCompraDetalle}
                                    onChange={(nuevoValor) => onCantidadChange(vino.id, nuevoValor)}
                                    anchoDinamico
                                />
                                <button
                                    className="detalle-agregar-boton"
                                    onClick={() => onAddToCart(vino)}
                                >
                                    AGREGAR
                                </button>
                            </div>
                        ) : (
                            <div className="detalle-sin-stock">
                                <p>SIN STOCK</p>
                            </div>
                        );
                    })()}
                </div>
            </div>
        </div>
    );
};

const Shop = () => {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    // Misma lógica que el dispatcher de navbar en App.js: PublicNavbar (más
    // baja) para anónimo/Registrado, MemberNavbar (más alta) para
    // Espera/Activo — la flecha de "volver" tiene que saber cuál está
    // activa para no quedar mal posicionada.
    const usaNavbarPublica = !user || user.profileData?.estadoLista === 'Registrado';
    const [vinos, setVinos] = useState([]);
    const [cargandoVinos, setCargandoVinos] = useState(true);
    const [error, setError] = useState(null);
    const [vinoSeleccionado, setVinoSeleccionado] = useState(null);
    const { addToCart } = useCart();
    const [cantidades, setCantidades] = useState({});
    const [filtro, setFiltro] = useState('todos');
    useDetalleHistorial(!!vinoSeleccionado, () => setVinoSeleccionado(null));

    useEffect(() => {
        // A diferencia de Cupo asignado, Shop no exige login para navegar el
        // catálogo (igual que Degustaciones/Demostraciones): solo hace falta
        // estar logueado para pagar en /carrito. Sin sesión, cupos_vinos
        // queda vacío y la compra se limita solo por stock.
        if (authLoading) return undefined;
        let vivo = true;
        const cargarVinos = async () => {
            const cuposVinosUsuario = user?.profileData?.cupos_vinos || [];
            const vinosRef = collection(db, 'vinos');
            const q = query(vinosRef, where('etiqueta_privada', '==', null));
            const querySnapshot = await getDocs(q);
            // Shop es el stock general: solo vinos que el ERP creó SIN cupo
            // asignado (vino.cupoAsignado, el campo propio del vino en Firestore
            // — no el cupo personal del cliente, que se calcula más abajo). Los
            // que sí tienen cupo son la línea limitada, exclusiva de /cupo-asignado.
            const vinosData = filtrarVinosDemo(
                querySnapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .filter(vino => vino.tipo_vino !== 'Crema de Estrellas' && !(Number(vino.cupoAsignado) > 0)),
                user?.uid
            );
            const stockPromises = vinosData.map(async (vino) => {
                const stockDocSnap = await getDoc(doc(db, 'stock', vino.id));
                let cantidadUnidades = stockDocSnap.exists() ? (stockDocSnap.data().cantidadUnidades ?? 0) : 0;
                const cupoAsignado = getCupoForVino(vino.id, cuposVinosUsuario);
                return {
                    ...vino,
                    cantidadUnidades: cantidadUnidades,
                    cupoAsignado: cupoAsignado,
                    // Marca de origen: Carrito.js la usa para aplicar el mínimo
                    // de compra de 6 botellas, que es propio del stock de Shop.
                    origen: 'shop'
                };
            });
            const vinosConStock = await Promise.all(stockPromises);
            const vinosDisponibles = vinosConStock.filter(vino =>
                vino.cantidadUnidades > 0 || (vino.cupoAsignado !== null && vino.cupoAsignado > 0)
            );
            const vinosOrdenados = [...vinosDisponibles].sort((a, b) => {
    // 1. Agrupamos los "Chirz" al principio
    const textoA = (a.marca || a.vino_nombre || '').toLowerCase();
    const textoB = (b.marca || b.vino_nombre || '').toLowerCase();
    
    const esChirzA = textoA.includes('chirz');
    const esChirzB = textoB.includes('chirz');

    if (esChirzA && !esChirzB) return -1;
    if (!esChirzA && esChirzB) return 1;

    // 2. Ordenamos por variedad, si no tiene, usamos la cepa.
    // Usamos String() por si 'cepas' viene como un array (ej: ['Malbec'])
    const criterioA = (a.variedad || String(a.cepas || '')).toLowerCase();
    const criterioB = (b.variedad || String(b.cepas || '')).toLowerCase();

    return criterioA.localeCompare(criterioB);
});
            await precargarImagenes(vinosOrdenados.map(v => v.etiqueta_imagen_url));
            return vinosOrdenados;
        };
        // La clave lleva el uid porque el cupo personal y el filtro de vinos
        // demo dependen de quien esta mirando.
        const clave = `${CLAVE_VINOS}shop:${user?.uid || 'anonimo'}`;
        const cacheado = leerCache(clave);
        if (cacheado) {
            // Ya lo trajimos antes en esta visita: se pinta sin spinner.
            setVinos(cacheado.valor);
            setCargandoVinos(false);
        }
        if (!cacheado || !cacheado.fresco) {
            obtenerConCache(clave, cargarVinos)
                .then((vinosOrdenados) => {
                    if (!vivo) return;
                    setVinos(vinosOrdenados);
                    setError(null);
                    setCargandoVinos(false);
                })
                .catch((err) => {
                    console.error("Error al obtener los vinos o el stock:", err);
                    // Si ya habia algo en pantalla, la revalidacion que falla
                    // se traga el error: mejor datos de hace un rato que un
                    // cartel rojo sobre una lista que se ve bien.
                    if (!vivo || cacheado) return;
                    setError("Error al cargar los datos. Inténtalo de nuevo más tarde.");
                    setCargandoVinos(false);
                });
        }
        return () => { vivo = false; };
    }, [authLoading, user]);

    const handleSelectVino = (vino) => {
        setVinoSeleccionado(vino);
    };

    const handleCantidadChange = (vinoId, valor) => {
        let cantidad = parseInt(valor);
        const vino = vinos.find(v => v.id === vinoId);
        if (!vino) return;
        const cupo = vino.cupoAsignado;
        let maximoPermitido = vino.cantidadUnidades;
        if (cupo !== null && cupo !== undefined) {
            maximoPermitido = Math.min(vino.cantidadUnidades, cupo);
        }
        if (isNaN(cantidad) || cantidad < 1) {
            cantidad = 1;
        }
        if (cantidad > maximoPermitido) {
            cantidad = maximoPermitido;
        }
        if (cantidad < 1) cantidad = 1;
        setCantidades(prevCantidades => ({
            ...prevCantidades,
            [vinoId]: cantidad,
        }));
    };

    const handleAddToCart = (vino) => {
        // Sin sesión, directo a loguearse/registrarse en vez de dejarlo
        // acumular en el carrito de localStorage — igual iba a terminar ahí
        // al querer pagar (PrivateRoute en /carrito), esto lo ahorra antes.
        if (!user) {
            navigate('/acceso-miembros', { state: { from: '/shop' } });
            return;
        }
        const cupo = vino.cupoAsignado;
        let maximoPermitido = vino.cantidadUnidades;
        if (cupo !== null && cupo !== undefined) {
            maximoPermitido = Math.min(vino.cantidadUnidades, cupo);
        }
        let cantidadSeleccionada = cantidades[vino.id] || 1;
        if (cantidadSeleccionada > maximoPermitido) {
            cantidadSeleccionada = maximoPermitido;
        }
        if (maximoPermitido === 0) return;
        addToCart(vino, cantidadSeleccionada);
    };

    if (vinoSeleccionado) {
        return (
            <div className="detalle-pagina-wrapper shop-detalle-wrapper historia-container">
                <div className="detalle-evento-contenido">
                    <DetalleVino
                        vino={vinoSeleccionado}
                        onVolver={() => window.history.back()}
                        cantidades={cantidades}
                        onCantidadChange={handleCantidadChange}
                        onAddToCart={handleAddToCart}
                        usaNavbarPublica={usaNavbarPublica}
                    />
                </div>
            </div>
        );
    }

    if (error) return <h2 className="error-text">❌ {error}</h2>;
    if (authLoading || cargandoVinos) return <LoadingSpinner />;

    const vinosFiltrados = vinos.filter(vino => {
        if (filtro === 'tinto') return TIPOS_TINTO.includes(vino.tipo_vino);
        if (filtro === 'blanco') return TIPOS_BLANCO.includes(vino.tipo_vino);
        return true;
    });

    return (
        <div className="historia-container">
            <div className="contenido-scrollable">
                <div className="titulo-principal">
                    <hr className="linea-horizontal" />
                    <h1>BIENVENIDO</h1>
                    <hr className="linea-horizontal" />
                </div>
                <p className="contenido-centrado">
                    Le damos la bienvenida oficial a la tienda exclusiva para miembros de Finca Los Astros. Cada vino que encontrarás a continuación es el resultado de una búsqueda por reconocer las demandas de un mundo que se reinventa, desde los procesos iniciales hasta los gustos, y nos obliga a satisfacer.
                </p>
                <p className="contenido-centrado shop-condiciones-titulo">
                    <strong>CONDICIONES DE ENTREGA</strong>
                </p>
                <p className="contenido-centrado">
                    • COMPRA MÍNIMA: 6 botellas (1 caja, combinable o varietal)
                    <br className="spacer-10px"></br>
                    • ENVÍOS: Exclusivos para toda la República Argentina, sin costo adicional
                </p>
                <p className="contenido-centrado">
                    Lo invitamos a explorar las etiquetas disponibles y comenzar a ser parte de nuestra historia.
                </p>
                <div className="titulo-principal shop-coleccion-titulo">
                    <hr className="linea-horizontal" />
                    <h1>NUESTRA COLECCIÓN</h1>
                    <hr className="linea-horizontal" />
                </div>
                <div className="filtros-shop">
                    {[
                        { valor: 'todos', label: 'Todos' },
                        { valor: 'tinto', label: 'Tintos' },
                        { valor: 'blanco', label: 'Blancos' },
                    ].map(opcion => (
                        <button
                            key={opcion.valor}
                            type="button"
                            onClick={() => setFiltro(opcion.valor)}
                            className={filtro === opcion.valor ? 'filtro-shop-boton filtro-shop-activo' : 'filtro-shop-boton'}
                        >
                            {opcion.label.toUpperCase()}
                        </button>
                    ))}
                </div>
                {vinosFiltrados.length === 0 ? (
                    <h2 className="contenido-centrado shop-empty-message" style={{textAlign: 'center'}}>No hay vinos disponibles en esta categoría por el momento.</h2>
                ) : (
                    <div className="grid-container">
                        {vinosFiltrados.map((vino) => {
                            const clasificacionFinal = vino.tipo_vino === 'Crema de Estrellas' ? vino.clasificacion_azucar : vino.clasificacion;
                            const cepasFormateadas = formatCepas(vino.cepas);
                            const contenidoCepasCompleto = vino.tipo_vino === 'Crema de Estrellas'
                                ? 'Crema de Estrellas'
                                : (cepasFormateadas && cepasFormateadas.length > 0 ? cepasFormateadas.join(' • ') : null);
                            // Si el resto del nombre ("Malbec" en "Red Social Malbec") ya ES
                            // la cepa, no la repetimos en su propia línea debajo.
                            const { marca: marcaVino, resto: restoVino } = obtenerMarcaYVariedad(vino);
                            const contenidoCepas = (contenidoCepasCompleto && contenidoCepasCompleto.toLowerCase() === restoVino.toLowerCase())
                                ? null
                                : contenidoCepasCompleto;
                            const cupo = vino.cupoAsignado;
                            const maximoCompra = cupo !== null && cupo !== undefined
                                ? Math.min(vino.cantidadUnidades, cupo)
                                : vino.cantidadUnidades;
                            return (
                                <div
                                    key={vino.id}
                                    className="card"
                                >
                                    {vino.etiqueta_imagen_url ? (
                                        <div className="wine-image-container">
                                            <img
                                                src={vino.etiqueta_imagen_url}
                                                alt={`Etiqueta de ${vino.vino_nombre}`}
                                                className="wine-image"
                                            /></div>
                                    ) : (
                                        <div className="image-placeholder">
                                        </div>
                                    )}
                                    <div className="card-datos">
                                        <h3 className="wine-name">
                                            <span className="wine-name-marca">{marcaVino}</span>
                                            {restoVino && (
                                                <span className="wine-name-resto">{restoVino}</span>
                                            )}
                                        </h3>
                                        {contenidoCepas && (<p className="data-text">{contenidoCepas}</p>)}
                                        {clasificacionFinal === 'Gran Reserva' && <p className="data-text">{clasificacionFinal}</p>}
                                        <p className="data-text">{vino.anada}</p>
                                        <p className="data-text card-stock-anclado">
                                            <p className="stock-units">
                                                {`${vino.cantidadUnidades} botellas disponibles`}
                                            </p>
                                        </p>
                                    </div>
                                    <button
                                        className="view-details-button"
                                        onClick={() => handleSelectVino(vino)}
                                    >
                                        VER DETALLES
                                    </button>
                                    <p className="price-unit">
                                        {vino.precioUnidad ?
                                            `AR$ ${new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(vino.precioUnidad)}`
                                            : ''}
                                    </p>
                                    {maximoCompra > 0 ? (
                                        <div className="grid-cantidad-fila">
                                            <CantidadStepper
                                                value={cantidades[vino.id] || 1}
                                                min={1}
                                                max={maximoCompra}
                                                onChange={(nuevoValor) => handleCantidadChange(vino.id, nuevoValor)}
                                                anchoDinamico
                                            />
                                            <button
                                                className="add-to-cart-button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleAddToCart(vino);
                                                }}
                                            >
                                                AGREGAR
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="grid-sin-stock">
                                            <p>SIN STOCK</p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Shop;
